import {
  Authenticated,
  ErrorComponent,
  Refine,
} from "@refinedev/core";
import {
  ThemedLayout,
  ThemedSider,
  ThemedTitle,
  useNotificationProvider,
} from "@refinedev/antd";
import {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import routerProvider from "@refinedev/react-router";
import { ConfigProvider, App as AntApp, Alert, Button } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { Component, type ReactNode } from "react";
import "@refinedev/antd/dist/reset.css";

class ErrorBoundary extends Component<{ children: ReactNode },
                                       { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, maxWidth: 720, margin: "40px auto" }}>
          <Alert
            type="error" showIcon
            message={`Ошибка рендеринга: ${this.state.error.name}`}
            description={
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12,
                            margin: 0, marginTop: 8 }}>
                {this.state.error.message + "\n\n" +
                 (this.state.error.stack || "")}
              </pre>
            }
          />
          <Button style={{ marginTop: 16 }} type="primary"
                  onClick={() => location.reload()}>Перезагрузить</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

import { authProvider } from "./authProvider";
import { dataProvider } from "./dataProvider";
import { LoginPage } from "./pages/login";
import { DashboardPage } from "./pages/dashboard";
import { OnlinePage } from "./pages/online/list";
import { SubscriptionList } from "./pages/subscriptions/list";
import { SubscriptionShow } from "./pages/subscriptions/show";
import { SubscriptionCreate } from "./pages/subscriptions/create";
import { UserList } from "./pages/users/list";
import { UserShow } from "./pages/users/show";
import { PaymentList } from "./pages/payments/list";
import { NodeList } from "./pages/nodes/list";
import { AuditLogList } from "./pages/audit_log/list";
import { DialogsPage } from "./pages/dialogs/list";
import { TagsPage } from "./pages/tags/list";
import { BroadcastList } from "./pages/broadcasts/list";
import { BroadcastCreate } from "./pages/broadcasts/create";
import { BroadcastShow } from "./pages/broadcasts/show";
import { PartnersList } from "./pages/partners/list";
import { PartnersShow } from "./pages/partners/show";
import { BotMessagesList } from "./pages/messages/list";
import { BotMessagesEdit } from "./pages/messages/edit";
import { UnreadBadgeLabel } from "./components/UnreadBadgeLabel";
import {
  DashboardOutlined,
  TeamOutlined,
  KeyOutlined,
  CreditCardOutlined,
  CloudServerOutlined,
  AuditOutlined,
  MessageOutlined,
  NotificationOutlined,
  TagsOutlined,
  EditOutlined,
  WifiOutlined,
} from "@ant-design/icons";

const App = () => (
  <BrowserRouter>
    <ConfigProvider theme={{
      token: {
        colorPrimary: "#1677ff",
        borderRadius: 6,
        fontSize: 14,
      },
      components: {
        Layout: { headerBg: "#fff", siderBg: "#001529" },
        Menu: { darkItemBg: "#001529" },
      },
    }}>
      <AntApp>
        <Refine
          dataProvider={dataProvider}
          authProvider={authProvider}
          routerProvider={routerProvider}
          notificationProvider={useNotificationProvider}
          resources={[
            {
              name: "dashboard",
              list: "/",
              meta: { label: "Сводка", icon: <DashboardOutlined /> },
            },
            {
              name: "online",
              list: "/online",
              meta: { label: "Online сейчас", icon: <WifiOutlined /> },
            },
            {
              name: "users",
              list: "/users",
              show: "/users/:id",
              meta: { label: "Юзеры", icon: <TeamOutlined /> },
            },
            {
              name: "subscriptions",
              list: "/subscriptions",
              show: "/subscriptions/:id",
              create: "/subscriptions/create",
              meta: { label: "UUID ключи", icon: <KeyOutlined />, canDelete: false },
              identifier: "subscriptions",
            },
            {
              name: "dialogs",
              list: "/dialogs",
              meta: {
                label: <UnreadBadgeLabel base="Диалоги" />,
                icon: <MessageOutlined />,
              },
            },
            {
              name: "broadcasts",
              list: "/broadcasts",
              create: "/broadcasts/create",
              show: "/broadcasts/:id",
              meta: { label: "Рассылки", icon: <NotificationOutlined /> },
            },
            {
              name: "partners",
              list: "/partners",
              show: "/partners/:id",
              meta: { label: "Партнёры", icon: <TeamOutlined /> },
            },
            {
              name: "messages",
              list: "/messages",
              show: "/messages/:id",
              meta: { label: "Тексты бота", icon: <EditOutlined /> },
            },
            {
              name: "tags",
              list: "/tags",
              meta: { label: "Метки", icon: <TagsOutlined /> },
            },
            {
              name: "payments",
              list: "/payments",
              meta: { label: "Платежи", icon: <CreditCardOutlined /> },
            },
            {
              name: "nodes",
              list: "/nodes",
              meta: { label: "Ноды", icon: <CloudServerOutlined /> },
            },
            {
              name: "audit_log",
              list: "/audit_log",
              meta: { label: "Аудит", icon: <AuditOutlined /> },
            },
          ]}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            useNewQueryKeys: true,
            disableTelemetry: true,
          }}
        >
          <Routes>
            <Route
              element={
                <Authenticated key="auth-inner" fallback={<CatchAllNavigate to="/login" />}>
                  <ThemedLayout
                    Title={({ collapsed }) => (
                      <ThemedTitle collapsed={collapsed} text="Bemli admin" />
                    )}
                    Sider={(p) => <ThemedSider {...p} fixed />}
                  >
                    <Outlet />
                  </ThemedLayout>
                </Authenticated>
              }
            >
              <Route index element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
              <Route path="/online" element={<ErrorBoundary><OnlinePage /></ErrorBoundary>} />
              <Route path="/users" element={<ErrorBoundary><UserList /></ErrorBoundary>} />
              <Route path="/users/:id" element={<ErrorBoundary><UserShow /></ErrorBoundary>} />
              <Route path="/subscriptions" element={<ErrorBoundary><SubscriptionList /></ErrorBoundary>} />
              <Route path="/subscriptions/:id" element={<ErrorBoundary><SubscriptionShow /></ErrorBoundary>} />
              <Route path="/subscriptions/create" element={<ErrorBoundary><SubscriptionCreate /></ErrorBoundary>} />
              <Route path="/payments" element={<ErrorBoundary><PaymentList /></ErrorBoundary>} />
              <Route path="/nodes" element={<ErrorBoundary><NodeList /></ErrorBoundary>} />
              <Route path="/audit_log" element={<ErrorBoundary><AuditLogList /></ErrorBoundary>} />
              <Route path="/dialogs" element={<ErrorBoundary><DialogsPage /></ErrorBoundary>} />
              <Route path="/tags" element={<ErrorBoundary><TagsPage /></ErrorBoundary>} />
              <Route path="/broadcasts" element={<ErrorBoundary><BroadcastList /></ErrorBoundary>} />
              <Route path="/broadcasts/create" element={<ErrorBoundary><BroadcastCreate /></ErrorBoundary>} />
              <Route path="/broadcasts/:id" element={<ErrorBoundary><BroadcastShow /></ErrorBoundary>} />
              <Route path="/partners" element={<ErrorBoundary><PartnersList /></ErrorBoundary>} />
              <Route path="/partners/:id" element={<ErrorBoundary><PartnersShow /></ErrorBoundary>} />
              <Route path="/messages" element={<ErrorBoundary><BotMessagesList /></ErrorBoundary>} />
              <Route path="/messages/:id" element={<ErrorBoundary><BotMessagesEdit /></ErrorBoundary>} />
              <Route path="*" element={<ErrorComponent />} />
            </Route>
            <Route
              element={
                <Authenticated key="auth-public" fallback={<Outlet />}>
                  <NavigateToResource resource="dashboard" />
                </Authenticated>
              }
            >
              <Route path="/login" element={<LoginPage />} />
            </Route>
          </Routes>
          <UnsavedChangesNotifier />
          <DocumentTitleHandler />
        </Refine>
      </AntApp>
    </ConfigProvider>
  </BrowserRouter>
);

export default App;
